<template>
  <BasePrompt
    :title="promptTitle"
    :question="i18n.failedInstallationPrompt.description"
    :open="isPromptOpen"
    :close="handlePromptClose"
    :cancel="handleCancel"
    :proceed="handleConfirm"
    :cancel-text="i18n.failedInstallationPrompt.cancelText"
    :proceed-text="promptProceedText"
    :proceed-disabled="proceedDisabled"
  >
    <BaseRadioSelector
      v-if="shouldDisplaySelection"
      v-model="selectedInstallationPointId"
      class="flex-col items-start px-7"
      :options-list="radioOptions"
    />
    <div
      v-else-if="singleCandidate"
      class="flex items-center gap-2 px-7 text-lg"
    >
      <em
        v-if="singleCandidateIcon"
        data-testid="impeded-single-type-icon"
        class="mdi"
        :class="singleCandidateIcon"
      />
      <span>„{{ formatReason(singleCandidate) }}“</span>
    </div>
  </BasePrompt>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import BaseRadioSelector from '@/components/UI/Radio/BaseRadioSelector.vue';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { DeviceType } from '@/models/devices/Device';
import { FormAction } from '@/store/form/formStore';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { useOrderStore } from '@/store/order/orderStore';
import { EntityTransactionType } from '@/models/OrderChanges';
import { impedimentIconClass } from '@/enums/generic';
import i18n from '@/utils/i18n/form.json';

/**
 * Only shown when the failed attempt carries a type — legacy / BE-created
 * attempts (no type) render nothing.
 */
const typeIcon = (impededPoint: InstallationPoint) => {
  const type = impededPoint.failedInstallationAttempt?.type;
  return type ? impedimentIconClass(type) : undefined;
};

const props = defineProps<{
  roomId: string;
  deviceType?: DeviceType;
  formAction: FormAction;
}>();

const emit = defineEmits<{
  (e: 'reuse-installation-point', installationPoint: InstallationPoint): void;
}>();

const installationPointStore = useInstallationPointStore();
const layoutStore = useLayoutStore();
const orderStore = useOrderStore();

const isPromptOpen = ref(false);
const selectedInstallationPointId = ref<string>();

const shouldCheckImpediments = () => {
  return props.formAction === FormAction.CREATE && !!props.deviceType;
};

const impededInstallationPoints = computed(() => {
  if (!shouldCheckImpediments()) {
    return [];
  }

  const failedPoints =
    installationPointStore
      .getFailedInstallationAttempt(props.roomId)
      ?.filter((ip) => ip.supportedDeviceType === props.deviceType) ?? [];

  return failedPoints.filter(
    (ip) =>
      !orderStore.hasTransaction(ip.id, EntityTransactionType.CREATE_ENTITY)
  );
});

const shouldDisplaySelection = computed(
  () => impededInstallationPoints.value.length > 1
);
const singleCandidate = computed(() =>
  impededInstallationPoints.value.length === 1
    ? impededInstallationPoints.value[0]
    : undefined
);
const singleCandidateIcon = computed(() =>
  singleCandidate.value ? typeIcon(singleCandidate.value) : undefined
);
const promptTitle = computed(() =>
  shouldDisplaySelection.value
    ? i18n.failedInstallationPrompt.multipleTitle
    : i18n.failedInstallationPrompt.singleTitle
);
const promptProceedText = computed(() =>
  shouldDisplaySelection.value
    ? i18n.failedInstallationPrompt.multipleProceed
    : i18n.failedInstallationPrompt.singleProceed
);
const proceedDisabled = computed(
  () => shouldDisplaySelection.value && !selectedInstallationPointId.value
);
const radioOptions = computed(() =>
  impededInstallationPoints.value.map((impededPoint, index) => ({
    name: `${formatPositionLabel(index)} „${formatReason(impededPoint)}“`,
    value: impededPoint.id,
    icon: typeIcon(impededPoint),
  }))
);

const formatPositionLabel = (index: number) => {
  return `${i18n.failedInstallationPrompt.positionLabel} ${index + 1}`;
};

const formatReason = (impededPoint: InstallationPoint) => {
  return (
    impededPoint.failedInstallationAttempt?.reason ||
    i18n.failedInstallationPrompt.reasonFallback
  );
};

const resetPrompt = () => {
  selectedInstallationPointId.value = undefined;
  isPromptOpen.value = false;
};

const handleCancel = () => {
  resetPrompt();
};

const handlePromptClose = () => {
  resetPrompt();
  layoutStore.closeFormDialog();
};

const handleConfirm = () => {
  const target = shouldDisplaySelection.value
    ? impededInstallationPoints.value.find(
        (ip) => ip.id === selectedInstallationPointId.value
      )
    : impededInstallationPoints.value[0];
  if (!target) {
    handleCancel();
    return;
  }

  resetPrompt();
  emit('reuse-installation-point', target);
};

const checkForImpediments = () => {
  if (!shouldCheckImpediments()) {
    return;
  }

  if (!impededInstallationPoints.value.length) {
    resetPrompt();
    return;
  }

  selectedInstallationPointId.value =
    impededInstallationPoints.value.length === 1
      ? impededInstallationPoints.value[0].id
      : undefined;
  isPromptOpen.value = true;
};

watch(
  () => impededInstallationPoints.value,
  () => {
    if (isPromptOpen.value) return;
    checkForImpediments();
  },
  { immediate: true }
);
</script>
